import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase-config';

/**
 * Get the current active module for a live package based on current date
 * @param {string} packageId - The package ID
 * @returns {Object|null} - Current active module with joining link or null
 */
export const getCurrentActiveModule = async (packageId) => {
    try {
        // Get package data
        const packageDoc = await getDoc(doc(db, 'lms_package', packageId));
        if (!packageDoc.exists()) {
            console.error('Package not found:', packageId);
            return null;
        }

        const packageData = packageDoc.data();
        
        // Only process live packages
        if (packageData.packageType !== 'package_lc') {
            return null;
        }

        const batchIds = packageData.packageContainingBatches || [];
        if (batchIds.length === 0) {
            return null;
        }

        const currentDate = new Date();
        let currentModule = null;
        let nextModule = null;

        // Get all batches for this package
        for (const batchId of batchIds) {
            const batchDoc = await getDoc(doc(db, 'lms_batch', batchId));
            if (!batchDoc.exists()) continue;

            const batchData = batchDoc.data();
            const moduleSettings = batchData.moduleSettings || {};

            // Process each module in the batch
            for (const [settingId, setting] of Object.entries(moduleSettings)) {
                if (!setting.moduleStartDate || !setting.moduleEndDate) continue;

                const startDate = new Date(setting.moduleStartDate);
                const endDate = new Date(setting.moduleEndDate);

                // Check if current date is within module period
                if (currentDate >= startDate && currentDate <= endDate) {
                    // Get module details
                    const moduleDoc = await getDoc(doc(db, 'lms_module', setting.moduleId));
                    if (moduleDoc.exists()) {
                        const moduleData = moduleDoc.data();
                        currentModule = {
                            ...setting,
                            moduleName: moduleData.moduleName,
                            liveLink: moduleData.liveLink,
                            moduleType: moduleData.moduleType,
                            startDate: startDate,
                            endDate: endDate
                        };
                        break;
                    }
                }

                // Find next upcoming module
                if (currentDate < startDate) {
                    const moduleDoc = await getDoc(doc(db, 'lms_module', setting.moduleId));
                    if (moduleDoc.exists()) {
                        const moduleData = moduleDoc.data();
                        const potentialNext = {
                            ...setting,
                            moduleName: moduleData.moduleName,
                            liveLink: moduleData.liveLink,
                            moduleType: moduleData.moduleType,
                            startDate: startDate,
                            endDate: endDate
                        };

                        if (!nextModule || startDate < nextModule.startDate) {
                            nextModule = potentialNext;
                        }
                    }
                }
            }

            if (currentModule) break;
        }

        // Return current module or next upcoming module
        return currentModule || nextModule;

    } catch (error) {
        console.error('Error getting current active module:', error);
        return null;
    }
};

/**
 * Get all modules for a package with their schedules
 * @param {string} packageId - The package ID
 * @returns {Array} - Array of modules with schedules
 */
export const getPackageModuleSchedule = async (packageId) => {
    try {
        const packageDoc = await getDoc(doc(db, 'lms_package', packageId));
        if (!packageDoc.exists()) return [];

        const packageData = packageDoc.data();
        const batchIds = packageData.packageContainingBatches || [];
        const modules = [];

        for (const batchId of batchIds) {
            const batchDoc = await getDoc(doc(db, 'lms_batch', batchId));
            if (!batchDoc.exists()) continue;

            const batchData = batchDoc.data();
            const moduleSettings = batchData.moduleSettings || {};

            for (const [settingId, setting] of Object.entries(moduleSettings)) {
                const moduleDoc = await getDoc(doc(db, 'lms_module', setting.moduleId));
                if (moduleDoc.exists()) {
                    const moduleData = moduleDoc.data();
                    modules.push({
                        ...setting,
                        moduleName: moduleData.moduleName,
                        liveLink: moduleData.liveLink,
                        moduleType: moduleData.moduleType,
                        startDate: new Date(setting.moduleStartDate),
                        endDate: new Date(setting.moduleEndDate)
                    });
                }
            }
        }

        // Sort modules by start date
        return modules.sort((a, b) => a.startDate - b.startDate);

    } catch (error) {
        console.error('Error getting package module schedule:', error);
        return [];
    }
};